import { useState } from "react";
import { toast } from "sonner";
import { Send, MapPin, Phone, Clock } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { api, formatApiError } from "@/App";

const COURSE_OPTIONS = ["German", "French", "Korean"];

export default function EnquirySection() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", course: "German", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent! We'll call you back within a day.");
      setForm({ name: "", phone: "", email: "", course: "German", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border border-white/10 rounded-md px-5 py-4 text-sm text-[#F4F4F0] placeholder:text-[#A3A3A3]/60 outline-none focus:border-[#C44900] transition-colors duration-300";

  return (
    <section id="contact" className="relative py-28 lg:py-40 overflow-hidden" data-testid="enquiry-section">
      <div className="absolute right-[-10%] bottom-[-20%] w-[45vw] h-[45vw] max-w-[700px] max-h-[700px] rounded-full bg-[#0268E6]/12 blur-[130px] pointer-events-none" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C44900] mb-6" data-testid="enquiry-eyebrow">04 — Begin</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tighter text-[#F4F4F0] leading-[1.02]">
            Your first lesson<br />is a <em className="italic text-[#C44900]">conversation.</em>
          </h2>
          <p className="mt-8 max-w-md text-sm lg:text-base text-[#A3A3A3] leading-relaxed">
            Tell us which language is calling you. We'll arrange a free demo class and a level assessment — no obligation.
          </p>
          <div className="mt-12 space-y-5 text-sm text-[#A3A3A3]">
            <p className="flex items-start gap-3" data-testid="contact-address">
              <MapPin size={16} className="text-[#C44900] mt-0.5 shrink-0" />
              Marian Corniche Apartments, Sulthan Bathery Rd, Boloor, Ashok Nagar, Mangaluru, Karnataka 575006
            </p>
            <p className="flex items-center gap-3" data-testid="contact-phone">
              <Phone size={16} className="text-[#C44900] shrink-0" />
              <a href="tel:9886673234" className="hover:text-[#F4F4F0] transition-colors">98866 73234</a>
            </p>
            <p className="flex items-center gap-3" data-testid="contact-timings">
              <Clock size={16} className="text-[#C44900] shrink-0" />
              Open daily from 6:30 AM
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            className="bg-[#081B40] border border-white/10 rounded-xl p-8 lg:p-12 space-y-5"
            data-testid="enquiry-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Name *</label>
                <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" data-testid="enquiry-name-input" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Phone *</label>
                <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="98XXXXXXXX" data-testid="enquiry-phone-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Email</label>
              <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="enquiry-email-input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Course *</label>
              <select className={`${inputCls} appearance-none`} value={form.course} onChange={set("course")} data-testid="enquiry-course-select">
                {COURSE_OPTIONS.map((c) => <option key={c} value={c} className="bg-[#081B40]">{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Message</label>
              <textarea rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Anything we should know? (goals, timings, current level…)" data-testid="enquiry-message-input" />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="group w-full flex items-center justify-center gap-3 bg-[#C44900] text-[#F4F4F0] text-xs uppercase tracking-[0.2em] py-5 rounded-full hover:bg-[#e05600] transition-colors duration-300 disabled:opacity-60"
              data-testid="enquiry-submit-button"
            >
              {sending ? "Sending…" : "Request a Free Demo"}
              {!sending && <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
